<script setup lang="ts">
/* =============================================================
   DASHBOARD
   =============================================================
   The landing screen after sign-in: the figures and the short
   lists a company acts on, from the bookings it can see.

   Everything is computed here, client-side, from one
   listBookings() call. RLS already returns exactly this company's
   bookings — those it made and those at its berths — so the two
   halves are split by which side the company is on, not fetched
   apart. At demo volumes that is one small request; a real
   deployment would move the sums into a view.

   Which half shows follows the account type; `both` gets both.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import StatTile from '@/components/ui/StatTile.vue'
import BookingOverviewList from '@/components/bookings/BookingOverviewList.vue'
import RankedBars from '@/components/bookings/RankedBars.vue'
import type { RankedItem } from '@/components/bookings/RankedBars.vue'
import type { IconName } from '@/components/icons'
import { routes, to } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { useAmenities } from '@/lib/amenities'
import { listBookings } from '@/lib/bookings'
import { bookingState, formatEur } from '@/lib/bookingLabels'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { errorMessage } from '@/lib/errors'
import type { AmenityCode, Booking } from '@/lib/types'

interface Stat {
  label: string
  value: string
  icon: IconName
  note: string
}

useDocumentTitle('Dashboard')
const { user } = useAuth()
const { amenityLabel } = useAmenities()

const firstName = computed(() => user.value?.name.split(' ')[0] ?? 'there')
const showsInfrastructure = computed(() => user.value?.role !== 'shipowner')
const showsFleet = computed(() => user.value?.role !== 'infrastructure_owner')

const bookings = ref<Booking[]>([])
const loading = ref(true)
const error = ref('')

onMounted(async () => {
  try {
    bookings.value = await listBookings()
  } catch (e) {
    error.value = errorMessage(e, 'Could not load your bookings.')
  } finally {
    loading.value = false
  }
})

/* --- shared arithmetic ----------------------------------------------------- */

const DAY = 86_400_000
const now = Date.now()
const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()

const time = (iso: string) => new Date(iso).getTime()
/** Agreed and still to happen or happening: what counts as booked. */
const isLive = (b: Booking) => b.status === 'requested' || b.status === 'confirmed'
const isUpcoming = (b: Booking) => isLive(b) && time(b.departure) >= now
const soonestFirst = (a: Booking, b: Booking) => a.arrival.localeCompare(b.arrival)
const sum = (list: Booking[]) => list.reduce((total, b) => total + b.price_eur, 0)
const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** Group, count, rank, keep the top few. */
function rank<K>(
  list: Booking[],
  keys: (b: Booking) => K[],
  describe: (key: K, n: number) => RankedItem,
  limit = 5,
): RankedItem[] {
  const counts = new Map<K, number>()
  for (const b of list) for (const k of keys(b)) counts.set(k, (counts.get(k) ?? 0) + 1)
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([k, n]) => describe(k, n))
}

const companyId = computed(() => user.value?.companyId)

/* --- at my berths (infrastructure owner) ----------------------------------- */

const atMyBerths = computed(() =>
  bookings.value.filter((b) => b.berth?.company_id === companyId.value),
)

const awaitingMe = computed(() =>
  atMyBerths.value
    .filter((b) => b.status === 'requested' && time(b.arrival) >= now)
    .sort(soonestFirst),
)

const arrivals30 = computed(() =>
  atMyBerths.value
    .filter((b) => isLive(b) && time(b.arrival) >= now && time(b.arrival) <= now + 30 * DAY)
    .sort(soonestFirst),
)

const revenue = computed(() =>
  sum(atMyBerths.value.filter((b) => b.status === 'confirmed' && time(b.arrival) >= startOfMonth)),
)

const ownerStats = computed<Stat[]>(() => [
  {
    label: 'Bookings',
    value: String(atMyBerths.value.length),
    icon: 'bookings',
    note: `At your berths · ${atMyBerths.value.filter((b) => b.status === 'confirmed').length} confirmed`,
  },
  {
    label: 'Arrivals',
    value: String(arrivals30.value.length),
    icon: 'clock',
    note: 'Next 30 days',
  },
  {
    label: 'Revenue',
    value: formatEur(revenue.value),
    icon: 'euro',
    note: 'Estimated · confirmed, from this month',
  },
  {
    label: 'Awaiting you',
    value: String(awaitingMe.value.length),
    icon: 'alert',
    note: awaitingMe.value.length ? 'Requests to confirm or reject' : 'All answered',
  },
])

const topAmenities = computed(() =>
  rank<AmenityCode>(
    atMyBerths.value.filter((b) => b.status !== 'rejected' && b.status !== 'cancelled'),
    (b) => b.requested_amenities,
    (code, n) => ({ label: amenityLabel(code), value: n, display: count(n, 'booking') }),
  ),
)

const topBerths = computed(() =>
  rank<number>(
    atMyBerths.value.filter(isLive),
    (b) => (b.berth ? [b.berth.id] : []),
    (id, n) => {
      const berth = atMyBerths.value.find((b) => b.berth?.id === id)?.berth
      return { label: berth?.name ?? `Berth ${id}`, value: n, note: berth?.city, to: to.berth(id) }
    },
  ),
)

/* --- booked by us (shipowner) ---------------------------------------------- */

const mine = computed(() => bookings.value.filter((b) => b.company_id === companyId.value))
const myUpcoming = computed(() => mine.value.filter(isUpcoming).sort(soonestFirst))
const myPast = computed(() => mine.value.filter((b) => bookingState(b) === 'completed'))
const myPending = computed(() => myUpcoming.value.filter((b) => b.status === 'requested'))
/** Agreed stays, finished or not — what the fleet has committed to pay. */
const spend = computed(() => sum(mine.value.filter((b) => b.status === 'confirmed')))

const shipownerStats = computed<Stat[]>(() => [
  {
    label: 'Upcoming',
    value: String(myUpcoming.value.length),
    icon: 'calendar',
    note: myPending.value.length
      ? `${myPending.value.length} awaiting the berth owner`
      : 'All confirmed',
  },
  {
    label: 'Past stays',
    value: String(myPast.value.length),
    icon: 'check',
    note: 'Completed',
  },
  {
    label: 'Docking spend',
    value: formatEur(spend.value),
    icon: 'euro',
    note: 'Confirmed stays, net of VAT',
  },
  {
    label: 'Locations used',
    value: String(new Set(mine.value.filter((b) => b.status === 'confirmed').map((b) => b.berth_id)).size),
    icon: 'pin',
    note: 'Distinct berths booked',
  },
])

const byShip = computed(() =>
  rank<number>(
    mine.value.filter((b) => b.status === 'confirmed' || b.status === 'requested'),
    (b) => [b.vessel_id],
    (id, n) => {
      const ship = mine.value.filter((b) => b.vessel_id === id && b.status === 'confirmed')
      const name = mine.value.find((b) => b.vessel_id === id)?.vessel?.name ?? `Vessel ${id}`
      return { label: name, value: n, display: count(n, 'booking'), note: `${formatEur(sum(ship))} confirmed`, to: to.vessel(id) }
    },
  ),
)

const topLocations = computed(() =>
  rank<string>(
    mine.value.filter((b) => b.status === 'confirmed' || b.status === 'requested'),
    (b) => (b.berth ? [`${b.berth.name}|${b.berth.city}`] : []),
    (key, n) => {
      const [name, city] = key.split('|')
      return { label: name ?? key, value: n, display: count(n, 'stay'), note: city }
    },
  ),
)

/* --- what shows -------------------------------------------------------------- */

const stats = computed<Stat[]>(() => {
  if (!showsInfrastructure.value) return shipownerStats.value
  if (!showsFleet.value) return ownerStats.value
  // `both` — the headline figures from each side.
  const [ownerTotal, , ownerRevenue, ownerAwaiting] = ownerStats.value
  const [shipUpcoming, , shipSpend] = shipownerStats.value
  return [ownerAwaiting, ownerTotal, ownerRevenue, shipUpcoming, shipSpend].filter(
    (s): s is Stat => s !== undefined,
  )
})

const requestsLink = { path: routes.bookings, query: { status: 'requested' } }
</script>

<template>
  <PageHeader
    :title="`Welcome back, ${firstName}`"
    :description="
      showsInfrastructure && showsFleet
        ? 'Your berths, your fleet and their bookings at a glance.'
        : showsInfrastructure
          ? 'Bookings at your berths at a glance.'
          : 'Your fleet’s bookings at a glance.'
    "
  >
    <template #actions>
      <Button v-if="showsFleet" :to="routes.search" icon="search">Find a berth</Button>
      <Button v-else :to="routes.locationNew" icon="plus">Add a location</Button>
    </template>
  </PageHeader>

  <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

  <div class="stats">
    <StatTile v-for="stat in stats" :key="stat.label" v-bind="stat" :loading="loading" />
  </div>

  <!-- Berth owner: what needs an answer, what is arriving, what is asked for. -->
  <div v-if="showsInfrastructure && !loading" class="columns">
    <div class="stack">
      <Panel
        flush
        title="Waiting for your answer"
        description="Requests at your berths, soonest first."
      >
        <template v-if="awaitingMe.length > 0" #actions>
          <Button :to="requestsLink" variant="ghost" size="sm" icon-right="arrowRight">
            All requests
          </Button>
        </template>
        <BookingOverviewList v-if="awaitingMe.length > 0" :bookings="awaitingMe.slice(0, 5)" />
        <EmptyState
          v-else
          icon="check"
          title="Nothing to answer"
          description="New requests for your berths will appear here."
        />
      </Panel>

      <Panel flush title="Arriving in the next 30 days">
        <template #actions>
          <Button :to="routes.bookings" variant="ghost" size="sm" icon-right="arrowRight">
            All bookings
          </Button>
        </template>
        <BookingOverviewList v-if="arrivals30.length > 0" :bookings="arrivals30.slice(0, 5)" />
        <p v-else class="panel-status">No arrivals in the next 30 days.</p>
      </Panel>
    </div>

    <div class="stack">
      <Panel title="Most requested amenities" description="Across bookings at your berths.">
        <RankedBars :items="topAmenities" empty="No amenities requested yet." />
      </Panel>
      <Panel title="Busiest berths" description="Requested and confirmed bookings.">
        <RankedBars :items="topBerths" empty="No bookings yet." />
      </Panel>
    </div>
  </div>

  <!-- Ship operator: what is coming up, and where the fleet goes. -->
  <div v-if="showsFleet && !loading" class="columns">
    <Panel flush title="Upcoming bookings" description="Soonest first.">
      <template #actions>
        <Button :to="routes.bookings" variant="ghost" size="sm" icon-right="arrowRight">
          All bookings
        </Button>
      </template>
      <BookingOverviewList v-if="myUpcoming.length > 0" :bookings="myUpcoming.slice(0, 6)" />
      <EmptyState
        v-else
        icon="calendar"
        title="No upcoming bookings"
        description="Find a berth for one of your vessels and book a slot."
      >
        <template #action>
          <Button :to="routes.search" icon="search" size="sm">Find a berth</Button>
        </template>
      </EmptyState>
    </Panel>

    <div class="stack">
      <Panel title="Bookings by ship" description="Requested and confirmed.">
        <RankedBars :items="byShip" empty="No bookings yet." />
      </Panel>
      <Panel title="Frequently used locations">
        <RankedBars :items="topLocations" empty="No bookings yet." />
      </Panel>
    </div>
  </div>
</template>

<style scoped>
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
}

.columns {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: var(--space-sm);
  align-items: start;
}

.columns + .columns {
  margin-top: var(--space-md);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  min-width: 0;
}

@media (max-width: 62rem) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 30rem) {
  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-2xs);
  }
}
</style>
